import { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch } from '@/utils/api';

interface InvoiceDocumentPreviewProps {
  invoiceId: string;
  hasDocument: boolean;
  tooltipText?: string;
}

/**
 * Invoice document preview component that shows a PDF/image preview on hover.
 * Only loads the document when the user hovers over the icon (lazy loading).
 */
export function InvoiceDocumentPreview({
  invoiceId,
  hasDocument,
  tooltipText
}: InvoiceDocumentPreviewProps) {
  const { t } = useI18n();
  
  // Early return BEFORE any hooks to avoid "Rendered fewer hooks than expected" error
  if (!hasDocument) {
    return null;
  }

  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDocument = async () => {
    // Don't reload if already loaded or currently loading
    if (documentUrl || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch the document with authentication
      const response = await apiFetch(`/api/invoices/${invoiceId}/photo`);

      if (!response.ok) {
        throw new Error('Failed to load document');
      }

      // Get the blob and create an object URL
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      setDocumentUrl(objectUrl);
      setMimeType(response.headers.get('content-type') || 'application/pdf');
    } catch (err) {
      console.error('Error loading document preview:', err);
      setError(t('documents.failedToLoad', 'common'));
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup object URL when component unmounts
  useEffect(() => {
    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentUrl]);

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          className="cursor-pointer inline-flex items-center"
          title={tooltipText || t('documents.hoverToPreview', 'common')}
          onMouseEnter={loadDocument}
        >
          <FileText className="h-4 w-4 text-blue-600 hover:text-blue-700" />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        className="w-[800px] h-[600px] p-2"
        sideOffset={10}
        style={{ transform: 'translateY(35px)' }}
      >
        <div className="w-full h-full flex flex-col">
          {isLoading && (
            <div className="w-full h-full flex flex-col gap-2">
              <Skeleton className="w-full h-8" />
              <Skeleton className="w-full flex-1" />
            </div>
          )}

          {error && (
            <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
              {error}
            </div>
          )}

          {!isLoading && !error && documentUrl && (
            <div className="w-full h-full overflow-hidden rounded border bg-white">
              {mimeType?.startsWith('image/') ? (
                <img
                  src={documentUrl}
                  alt={t('documents.invoicePreview', 'common')}
                  className="w-full h-auto object-top"
                  style={{ maxWidth: '100%', display: 'block' }}
                />
              ) : (
                <iframe
                  src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="w-full h-full border-0"
                  title={t('documents.invoicePreview', 'common')}
                />
              )}
            </div>
          )}

          {!isLoading && !error && !documentUrl && (
            <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
              {t('documents.hoverToLoad', 'common')}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
